import "reflect-metadata";
import type { AppProps } from "next/app";
import { NavHeader } from "components/navHeader";
import Agreement from "components/Agreement";
import { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { UserTypeEnum } from "@/api/dmp_common/interface";
import { locale } from "moment";
import watermark from "@sec/watermark-sdk";
import * as pmm from "@afe/pmm-sdk";
import { isProduct } from "src/common/util/tool";
import Unavailable from "src/components/result/unavailable";
import ProjectErrorBoundary from "src/components/ErrorBoundary/ProjectErrorBoundary";
import { useRouter } from "next/router";
import GlobalContext, { GlobalContextType } from "models/globalContext";
import { onRedirect } from "components/navHeader/utils";
import Router from "next/router";
import { CAT_LOGGER_API } from "../config";
import Footer from "components/footer";
import DMPSkeleton from "src/views/skeleton";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { Toast } from "@ad/anq-ui";
import CheckAuth from "components/check_auth";
import { queryAgreement, signAgreement } from "@/api/sign_agreement";
import { queryUserTypeAndInfo } from "@/api/dmp_common";
import "globalthis/auto";

import "moment/locale/zh-cn";
import "../styles/globals.css";
import "../styles/rocket.css";
import Empty from "components/empty";

locale("zh-cn");

const routeChangHandler = (url, params) => {
  if (!params?.shallow) {
    const baseUrl = process.env.NODE_ENV !== "production" ? "/pro" : "";
    fetch(`${baseUrl}${CAT_LOGGER_API}?url=${encodeURIComponent(url)}`);
  }
};

function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const [isUnavailable, setUnavailable] = useState(false);
  const [promptStatement, setUnavailablePrompt] = useState("");
  const [isMount, setMount] = useState(false);
  const [globalContextValue, setGlobalContextValue] =
    useState<GlobalContextType>({});
  const [isSignAgreement, setIsSignAgreement] = useState(false);

  useEffect(() => {
    init();
    redirectUrl();
  }, []);

  useEffect(() => {
    // NOTICE: 首次进入页面或者主动刷新页面的时候, 如果url存在query或者是动态路由, 这里也会调用一次. 这一次不记录
    // 原因是next会在页面首次加载(data.nextExport && (isDynamicRoute(router.pathname) || location.search)) || (Component.__NEXT_SPR && location.search)的时候执行router.replace()
    Router.events.on("routeChangeComplete", routeChangHandler);

    return () => {
      Router.events.off("routeChangeComplete", routeChangHandler);
    };
  }, []);

  /** 已下线的老的的https://dmp.pinduoduo.com/dmp/*已经指向这里, 需要重定向到人群管理页面 */
  const redirectUrl = () => {
    if (router.asPath?.includes("/dmp")) {
      onRedirect();
    }
  };

  const init = async () => {
    const userInfoRes = await queryUserTypeAndInfo({});

    if (userInfoRes?.success) {
      const userInfo = userInfoRes.result || {};
      const { mallId, name } = userInfo;

      // 本地dev开发环境不上报
      if (process.env.NODE_ENV === "production") {
        pmm.init({
          // PMM项目ID（线上和胡桃街）(project_id和projectId等效)
          projectId: "101007",
          // PMM项目名字（1.3.0版本支持，name和id二选一即可，不用都传）
          projectName: "advanced-dmp",
          // PMM项目TOKEN
          token: "C4B2E68B14F9D335172B3F2326B6B133",
          // 构建时的版本号
          release: "",
          // 此处的环境仅为异常的一个tag属性，如果需要上报到PMM胡桃街需要修改下发参数
          environment: isProduct() ? "production" : "testing",
          // 可选
          initialScope: {
            // 设置当前用户user，也可以通过setUser异步设置
            user: {
              id: String(mallId),
              username: name,
            },
          },
        });
      }

      setGlobalContextValue({
        ...userInfo,
        isBrand: userInfo.userType === UserTypeEnum.BRAND,
        // 部分账号是null
        brandInfoList: userInfo.brandInfoList || [],
        initEnd: true,
      });

      watermark.init({
        width: 300,
        height: 300,
        fillStyle: "rgba(100,100,100,0.08)",
        font: "14px Times New Roman",
        parentEl: "watermark-app", //需要打水印的dom元素ID,不传默认为body
        text: `${mallId} ${name}`, //水印文字
      });

      await agreementQueryApi();
      setMount(true);
    } else {
      setUnavailable(true);
      setUnavailablePrompt(userInfoRes?.errorMsg || "服务器内部错误");
    }
  };

  const agreementQueryApi = async () => {
    const res = await queryAgreement();

    if (res?.success) {
      setIsSignAgreement(res?.result.signResult);
    } else {
      Toast.error(res?.errorMsg);
    }
  };

  const agreementSignApi = async () => {
    const res = await signAgreement();

    if (res?.success) {
      setIsSignAgreement(res.result.signSuccess);
      Toast.success("已签署，页面即将跳转", 1, {
        onClose: () => {
          setIsSignAgreement(res.result.signSuccess);
        },
      });
    } else {
      Toast.error(res?.errorMsg);
    }
  };

  /**
   * 渲染页面的内容部分
   * 优先级: 骨架屏 -> 无权限 -> 签协议 -> 正常页面(包括权限校验/异常捕获)
   */
  const renderContent = () => {
    if (!isMount) {
      return <DMPSkeleton />;
    }

    if (!globalContextValue.hasDmpAuth) {
      return (
        <Empty
          content={globalContextValue.noAuthMsg}
          title="暂无权限"
          className="h-[calc(100vh-56px)]"
          image="https://commimg.pddpic.com/upload/dmp/no_permission.png.slim.png"
        />
      );
    }

    if (!isSignAgreement && globalContextValue.userType !== UserTypeEnum.INIT) {
      return <Agreement onClick={agreementSignApi} />;
    }

    return (
      <SimpleBar
        autoHide={false}
        className="root-simplebar max-h-[calc(100vh-56px)]"
      >
        <CheckAuth>
          <ProjectErrorBoundary>
            <Component {...pageProps} />
          </ProjectErrorBoundary>
        </CheckAuth>
      </SimpleBar>
    );
  };

  return (
    <GlobalContext.Provider value={globalContextValue}>
      <div className="min-w-[1440px]">
        <title>拼多多数据资产管理平台</title>
        <NavHeader />
        {isUnavailable ? (
          <Unavailable promptStatement={promptStatement} />
        ) : (
          <div id="watermark-app" className="w-full">
            {renderContent()}
            <Footer />
          </div>
        )}
      </div>
    </GlobalContext.Provider>
  );
}
export default observer(App);
